import { useContext, useEffect, useState, useTransition } from "react";
import { Header } from "@components/pages/Header";
import {
  TrendingUp,
  Shield,
  FileSearch,
  MessageCircle,
  UserPlus,
  Zap,
} from "lucide-react";
import { SearchBar } from "@components/pages/Search";
import { DataTable, TaxRecord } from "@components/pages/DataTable";
import EndPointServices from "@services/ApiServices";
import { PlanCard } from "@components/pages/PlanCard";
import UtilServices from "@services/UtilServices";
import { ToastContainer } from "react-toastify";
import { GlobalContext } from "@context/GlobalContext";
import Loading from "@components/loading/Loading";
import NewModal from "@components/pages/NewModal";
import { useNavigate } from "react-router-dom";
import { set } from "react-hook-form";
import useLoading from "@hooks/useLoading";
import { HowItWorks } from "@components/pages/HowitWork";

// const weeklyPlan = {
//   name: "Plano Semanal",
//   price: "R$ 29",
//   period: "semana",
//   description: "Perfeito para consultas ocasionais e insights rápidos.",
//   features: [
//     { text: "Ilimitado por 1 semana" },
//     // { text: "Relatórios detalhados" },
//     { text: "Suporte" },
//     // { text: "Histórico de 7 dias" },
//   ],
//   ctaText: "Escolher Plano",
// };

type SearchType = "servico" | "comercio" | "outras";

const Ibscbs = () => {
  const [searchResults, setSearchResults] = useState<TaxRecord[] | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [type, setType] = useState<SearchType>("servico");
  const [state, dispatch] = useContext(GlobalContext);
  const [showModal, setShowModal] = useState(false);
  const [showWithoutLimits, setShowWithoutLimits] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const navigate = useNavigate();
  useLoading(isPending);

  useEffect(() => {
    // const statusFinal = ["rejected", "cancelled", "approved"];
    const statusFinal = ["approved", "cancelled"];
    const status = state.confirmaplano?.status_pagamento;

    // Se não tem status ou já é final, não cria intervalo
    if (!status || statusFinal.includes(status)) return;

    const interval = setInterval(() => {
      handlePayStatus();
    }, 60_000); // 1 minuto

    return () => clearInterval(interval);
  }, [state.confirmaplano?.status_pagamento]);

  //Planos
  const freePlan = {
    name: "Plano Gratuito",
    price: "R$ 0",
    period: "mês",
    description: "Ideal para começar e conhecer a plataforma.",
    features: [
      { text: "3 consultas por dia" },
      // { text: "Relatórios básicos" },
      // { text: "Histórico de 3 dias" },
    ],
    ctaText: "Começar Grátis",
    setShowModal: () => {
      setShowModal(true);
    },
  };
  const monthlyPlan = {
    name: "Plano Mensal",
    price: "R$ 89",
    period: "mês",
    description: "Acesso ilimitado para profissionais e empresas.",
    features: [
      { text: "Consultas ilimitadas", highlighted: true },
      // { text: "Relatórios analíticos completos", highlighted: true },
      // { text: "Exportar para PDF & Excel", highlighted: true },
      { text: "Suporte prioritário" },
      // { text: "Histórico de 90 dias" },
      // { text: "Acesso à API" },
    ],
    ctaText: "Fazer Upgrade",
    isPopular: true,
    setShowModal: () => {
      setShowModal(true);
    },
    onSelect: () => {
      handlePay();
    },
  };

  function handleLimitConsults() {
    setShowModal(true);
    setShowWithoutLimits(true);
  }
  const handlePay = async () => {
    startTransition(async () => {
      let data = {
        id_usuario_guid: state.dadosusuario?.id_usuario_guid,
        id_usuario: state.dadosusuario?.id_usuario,
      };
      var retornoapi = await EndPointServices.Partilha_PayMercadoPago(data);

      if (retornoapi?.checkoutUrl?.InitPoint) {
        window.location.href = retornoapi?.checkoutUrl?.InitPoint;

        let confirmaplano = {
          status_pagamento: "envio",
          externalreference: retornoapi.checkoutUrl?.ExternalReference,
          timestamp: new Date().getTime(),
        };

        UtilServices.setStorage("confirmaplano", confirmaplano);
        dispatch({
          type: "SET_CONFIRMAPLANO",
          payload: confirmaplano,
        });

        setShowPay(true);
        setShowWithoutLimits(false);
      }
    });
  };
  const handleSearchServico = async (query: string) => {
    if (Object.keys(state.dadosusuario).length === 0) {
      setShowModal(true);
      return;
    }

    startTransition(async () => {
      let data = {
        id_usuario_guid: state.dadosusuario?.id_usuario_guid,
        id_usuario: state.dadosusuario?.id_usuario,
        p_busca: query,
      };
      setHasSearched(true);

      var retornoapi = await EndPointServices.IBSCBS_ConsultaServico(
        data,
        handleLimitConsults,
      );
      if (retornoapi?.Success) {
        setSearchResults(retornoapi?.BaseReturn?.LstClasse);
      } else {
        setSearchResults([]);
      }
    });
  };
  const handleSearchComercio = async (query: string, type: SearchType) => {
    if (Object.keys(state.dadosusuario).length === 0) {
      setShowModal(true);
      return;
    }
    startTransition(async () => {
      let data = {
        id_usuario_guid: state.dadosusuario?.id_usuario_guid,
        id_usuario: state.dadosusuario?.id_usuario,
        p_busca: query,
        p_type: type,
      };
      setHasSearched(true);

      var retornoapi = await EndPointServices.IBSCBS_ConsultaComercio(
        data,
        handleLimitConsults,
      );

      if (retornoapi?.Success) {
        setSearchResults(retornoapi?.BaseReturn?.LstClasse);
      } else {
        setSearchResults([]);
      }
    });
  };
  const handlePayStatus = async () => {
    const temPlanoPremiumSistema3 =
      state.dadosusuario?.planos?.some(
        (plano) => plano.plano === "Premium" && plano.id_sistema === 3,
      ) ?? false;

    if (temPlanoPremiumSistema3) {
      UtilServices.setStorage("confirmaplano", []);
      dispatch({
        type: "SET_CONFIRMAPLANO",
        payload: [],
      });
      return;
    }

    let data = {
      id_usuario_guid: state.dadosusuario?.id_usuario_guid,
      id_usuario: state.dadosusuario?.id_usuario,
      externalreference: state.confirmaplano?.externalreference,
    };
    var retornoapi = await EndPointServices.Partilha_PayStatus(data);

    if (retornoapi?.Success) {
      let usuario = { ...state.dadosusuario };

      usuario.planos = retornoapi?.BaseReturn?.LstClasse?.filter(
        (item) => item.plano,
      );
      UtilServices.setStorage("dadosusuario", usuario);
      dispatch({
        type: "SET_USUARIO",
        payload: usuario,
      });

      const checkplan = retornoapi?.BaseReturn?.LstClasse?.find(
        (item: any) => item.status_pagamento,
      );

      let confirmaplano = {
        status_pagamento: checkplan?.status_pagamento,
        externalreference: checkplan?.externalreference,
        timestamp: new Date().getTime(),
      };

      UtilServices.setStorage("confirmaplano", confirmaplano);
      dispatch({
        type: "SET_CONFIRMAPLANO",
        payload: confirmaplano,
      });
    }
  };

  return (
    <div className="min-h-screen bg-background" id="consult">
      <Header />
      {/* Hero Section */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-tail/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary-tail/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-10 animate-fade-up">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-tight">
              Consulta de Dados{" "}
              <span className="text-gradient-orange">IBS/CBS</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Encontre alíquotas e classificações tributárias de forma rápida,
              segura e confiável.
            </p>
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
            <SearchBar
              onSearchServico={handleSearchServico}
              onSearchComercio={handleSearchComercio}
              setSearchResults={setSearchResults}
              isLoading={isPending}
              setType={setType}
            />
          </div>

          {/* Quick Stats */}
          <div
            className="flex flex-wrap justify-center gap-8 mt-12 animate-fade-up"
            style={{ animationDelay: "200ms" }}
          >
            {[
              { icon: FileSearch, label: "Records Indexed", value: "2.4M+" },
              { icon: TrendingUp, label: "Daily Updates", value: "Real-time" },
              { icon: Shield, label: "Data Security", value: "Enterprise" },
            ].map((stat, index) => (
              <div key={index} className="flex items-center gap-3 text-sm">
                <div className="w-10 h-10 rounded-lg bg-secondary-tail flex items-center justify-center">
                  <stat.icon className="w-5 h-5 text-primary-tail" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">{stat.label}</p>
                  <p className="text-foreground font-semibold">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      {/* {hasSearched && ( */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          {/* <div className="px-4"> */}
          <div className="bg-card border-1 border-border-tail rounded-2xl overflow-hidden">
            <div className="px-6 py-3 border-b border-border-tail flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {isPending
                    ? "Buscando..."
                    : searchResults
                      ? `${searchResults.length} resultados encontrados`
                      : "Sem resultados. Pesquise na barra de busca acima"}
                </p>
              </div>
            </div>

            <div className="p-6">
              {isPending ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="w-12 h-12 border-3 border-primary-tail/30 border-t-primary-tail rounded-full animate-spin" />
                  <p className="text-muted-foreground text-sm">Carregando...</p>
                </div>
              ) : searchResults ? (
                <DataTable data={searchResults} type={type} />
              ) : null}
            </div>
          </div>
        </div>
      </section>
      {/* )} */}

      {/* How It Works Section */}
      <HowItWorks />

      {/* Plans Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              Escolha Seu Plano
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Desbloqueie todo o potencial da inteligência fiscal com nossos
              planos flexíveis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="animate-fade-up" style={{ animationDelay: "0ms" }}>
              <PlanCard {...freePlan} />
            </div>
            {/* <div
              className="animate-fade-up"
              style={{ animationDelay: "100ms" }}
            >
              <PlanCard {...weeklyPlan} />
            </div> */}
            <div
              className="animate-fade-up"
              style={{ animationDelay: "200ms" }}
            >
              <PlanCard {...monthlyPlan} />
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        onClick={() => {
          if (window.gtag) {
            window.gtag("event", "conversion", {
              send_to: "AW-17893671730/D6dbCPTU8OkbELKGr9RC",
            });
          }
          UtilServices.GetWhatsApp(
            "5511933773522",
            "Olá preciso de suporte com o ibscbs.",
          );
        }}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed cursor-pointer bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-full shadow-lg hover:bg-[#20BA5C] transition-all duration-300 hover:scale-105 hover:shadow-xl group"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm font-medium hidden sm:inline">
          Suporte via WhatsApp
        </span>
      </a>

      {/* Footer */}
      <footer className="border-t border-border-tail py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2024 Ktechno. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
      <ToastContainer />
      <Loading color="#c1abff" show={state.loading} />

      <NewModal
        showModal={showModal}
        setShowModal={() => {
          setShowModal(false);
          setShowPay(false);
          setShowWithoutLimits(false);
        }}
        title={
          showWithoutLimits
            ? "Faça um Upgrade"
            : showPay
              ? "Aguardando Pagamento"
              : "Conta Necessária"
        }
      >
        {showPay ? (
          <div className="flex flex-col">
            <p className="text-muted-foreground text-sm mb-2">
              Aguarde a confirmação do pagamento. <br />
              Assim que confirmado, seu acesso será liberado.
            </p>
          </div>
        ) : showWithoutLimits ? (
          <div className="flex flex-col">
            <p className="text-muted-foreground text-sm mb-2">
              Seu limite de consultas foi atingido.
              <br />
              Faça upgrade do seu plano agora e tenha consultas ilimitadas.
            </p>
            <button
              className="mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-primary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 hover:border-primary-tail/50 transition-all"
              onClick={() => {
                handlePay();
              }}
            >
              <Zap className="w-4 h-4 text-foreground hover:text-primary-tail" />
              Faça upgrade agora
            </button>
          </div>
        ) : (
          <div className="flex flex-col">
            <p className="text-muted-foreground text-sm mb-2">
              Para continuar, você precisa criar uma conta.
            </p>
            <button
              className="mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-primary-tail border-1 border-border-tail rounded-lg text-sm font-medium text-foreground hover:bg-secondary-tail/80 hover:border-primary-tail/50 transition-all"
              onClick={() => {
                navigate("/Cadastrar");
              }}
            >
              Criar Conta
              <UserPlus className="w-4 h-4 text-foreground" />
            </button>
            <p className="text-muted-foreground text-sm text-center mt-4">
              Já possui uma conta?{" "}
              <span
                onClick={() => {
                  navigate("/");
                }}
                className="text-primary-tail hover:underline cursor-pointer"
              >
                Faça login.
              </span>
            </p>
          </div>
        )}
      </NewModal>
    </div>
  );
};

export default Ibscbs;
